import { Injectable, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { AuthService } from './auth.service';
import { PrivacyUpdateModalComponent } from '../components/privacy-update-modal/privacy-update-modal.component';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class PrivacyPolicyService {
  private modalRef?: BsModalRef;
  // Use a signal for the hash to allow reactive dependencies
  public readonly policyHash = signal<string>('');
  private isCheckingPrivacy: boolean = false;

  constructor(
    private http: HttpClient,
    private modalService: BsModalService,
    private authService: AuthService
  ) { }

  /**
   * Calculates a simple hash from a string.
   * This acts as a unique fingerprint for the text content.
   */
  private cyrb53(str: string, seed = 0): string {
    let h1 = 0xdeadbeef ^ seed, h2 = 0x41c6ce57 ^ seed;
    for (let i = 0, ch; i < str.length; i++) {
      ch = str.charCodeAt(i);
      h1 = Math.imul(h1 ^ ch, 2654435761);
      h2 = Math.imul(h2 ^ ch, 1597334677);
    }
    h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
    h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
    return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(16);
  }

  /**
   * Fetches the current privacy policy text and calculates its hash.
   */
  async init(): Promise<void> {
    try {
      // Use an absolute path so this works on nested routes like `/auth/register`.
      const text = await firstValueFrom(this.http.get('/assets/datenschutzerklaerung.md', { responseType: 'text' }));
      // Normalize line endings and trim to avoid issues with git crlf/lf or accidental whitespace
      const normalizedText = text.replace(/\r\n/g, '\n').trim();
      const hash = this.cyrb53(normalizedText);
      this.policyHash.set(hash);
      console.log('Current Privacy Policy Hash:', hash);
    } catch (error) {
      console.error('Failed to load privacy policy for hashing', error);
      // Fallback: If we can't load the file, we shouldn't block the app, 
      // but we also can't enforce the check properly.
    }
  }

  getCurrentHash(): string {
    return this.policyHash();
  }

  /**
   * Check if user needs to accept updated privacy policy
   */
  async showPrivacyUpdateModalIfNeeded(): Promise<boolean> {
    if (this.isPrivacyModalActive()) {
      return true; // Already showing
    }

    // Ensure we have the hash
    if (!this.policyHash()) {
      await this.init();
    }

    const user = this.authService.user();
    if (!user) {
      console.log('[PrivacyPolicy] No user found, skipping check');
      return false;
    }

    // Check if user has accepted the EXACT current content hash
    const userAcceptedHash = user.privacy_policy_hash;

    // We access the signal value here
    const currentHash = this.policyHash();

    console.log('[PrivacyPolicy] Hash comparison:', {
      userAcceptedHash,
      currentHash,
      match: userAcceptedHash === currentHash,
      userHashType: typeof userAcceptedHash,
      currentHashType: typeof currentHash,
      userHashLength: userAcceptedHash?.length,
      currentHashLength: currentHash?.length
    });

    if (!currentHash) {
      // If hash isn't loaded yet, we can't determine equality properly.
      // We'll wait for init() to complete (which sets the signal).
      // However, init() is usually awaited in app.component.
      console.log('[PrivacyPolicy] No current hash loaded, skipping check');
      return false;
    }

    if (userAcceptedHash === currentHash) {
      console.log('[PrivacyPolicy] ✅ Hashes match, no modal needed');
      return false; // Already accepted this exact version
    }

    console.log('[PrivacyPolicy] ⚠️ Hashes do NOT match, showing modal');

    // Set flag to indicate we're showing the modal
    this.isCheckingPrivacy = true;

    // Show modal
    this.modalRef = this.modalService.show(PrivacyUpdateModalComponent, {
      backdrop: 'static',
      keyboard: false,
      class: 'modal-lg'
    });

    return true;
  }

  /**
   * Check if privacy modal is currently being shown
   */
  isPrivacyModalActive(): boolean {
    return this.isCheckingPrivacy && !!this.modalRef;
  }

  /**
   * Check if user has accepted current privacy policy
   */
  hasUserAcceptedCurrentPolicy(): boolean {
    const user = this.authService.user();
    if (!user) return false;

    // If we haven't loaded the policy hash yet, we can't confirm acceptance
    if (!this.policyHash()) return false;

    return user.privacy_policy_hash === this.policyHash();
  }

  closeModal(): void {
    if (this.modalRef) {
      this.modalRef.hide();
      this.modalRef = undefined;
    }
    this.isCheckingPrivacy = false;
  }
}
